import dayjs from '@/lib/dayjs'
import { z } from 'zod/v4'
import type {
  AppUpdate,
  AppUpdateAudience,
  AppUpdateCategory,
  AppUpdateMedia,
  AppUpdateSort,
  AppUpdateType,
  CreateAppUpdateInput,
} from './app-updates.types'
import { getScribeEmbedSrc, isScribeEmbed } from './app-updates.utils'

export const APP_UPDATE_FEATURED_COUNT = 4
export const APP_UPDATE_PAGE_SIZE = 9

interface AppUpdateOption<TId extends string> {
  id: TId
  name: string
}

export const appUpdateAudienceOptions: AppUpdateOption<AppUpdateAudience>[] = [
  { id: 'organizations', name: 'Organizations' },
  { id: 'global', name: 'Global' },
]

export const appUpdateCategoryOptions: AppUpdateOption<AppUpdateCategory>[] = [
  { id: 'all', name: 'All' },
  { id: 'features', name: 'Features' },
  { id: 'bugs', name: 'Bug Fixes' },
  { id: 'system', name: 'System' },
]

export const appUpdateSortOptions: AppUpdateOption<AppUpdateSort>[] = [
  { id: 'recent', name: 'Most Recent' },
  { id: 'oldest', name: 'Oldest' },
]

export const appUpdateTypeOptions: AppUpdateOption<AppUpdateType>[] = [
  { id: 'bug', name: 'Bug Fix' },
  { id: 'new_feature', name: 'New Feature' },
  { id: 'performance', name: 'Performance' },
  { id: 'security', name: 'Security' },
  { id: 'ui_ux', name: 'UI/UX' },
]

const optionalTrimmedString = z
  .string()
  .trim()
  .optional()
  .nullable()
  .transform((value) => value || null)

const mediaSchema = z
  .object({
    id: z.coerce.number().int().positive(),
  })
  .loose()

const benefitSchema = z
  .string()
  .trim()
  .max(240, 'Keep each benefit under 240 characters')
  .optional()
  .nullable()
  .transform((value) => value || '')

export const appUpdateFormSchema = z.object({
  title: z.string().trim().min(1, 'Title is required').max(160),
  description: optionalTrimmedString,
  description_html: optionalTrimmedString,
  benefits: z.array(benefitSchema).max(5, 'Add up to 5 benefits').default(['', '', '']),
  audience: z.enum(['global', 'organizations']),
  update_type: z.enum(['bug', 'new_feature', 'performance', 'security', 'ui_ux']),
  destination_url: optionalTrimmedString,
  scribe_url: optionalTrimmedString.refine((value) => !value || isScribeEmbed(value), {
    message: 'Paste a Scribe embed code',
  }),
  is_published: z.boolean(),
  update_date: optionalTrimmedString,
  thumbnail: mediaSchema.nullable().default(null),
  gallery: z.array(mediaSchema).max(12, 'Choose up to 12 gallery images').default([]),
})

export const appUpdateParamsSchema = z.object({
  appUpdateId: z.coerce.number().int().positive(),
})

export const appUpdatesSearchSchema = z.object({
  page: z.coerce.number().int().positive().catch(1),
  pageSize: z.coerce.number().int().min(1).max(100).catch(APP_UPDATE_PAGE_SIZE),
  search: z.string().trim().catch(''),
  category: z.enum(['all', 'features', 'bugs', 'system']).catch('all'),
  sort: z.enum(['recent', 'oldest']).catch('recent'),
})

/**
 * The app update form's values. Text fields hold what the inputs show (blank as `''`);
 * `toAppUpdatePayload()` turns them into a request body.
 */
export interface AppUpdateFormValues {
  title: string
  description: string
  description_html: string
  /** Three to five rows; blank rows are dropped on save. */
  benefits: string[]
  audience: AppUpdateAudience
  update_type: AppUpdateType
  destination_url: string
  scribe_url: string
  is_published: boolean
  update_date: string
  thumbnail: AppUpdateMedia | null
  gallery: AppUpdateMedia[]
}

function cleanedValue(value: string | null | undefined): string | null {
  if (typeof value !== 'string') {
    return value ?? null
  }

  const trimmed = value.trim()

  return trimmed || null
}

function todayDateString() {
  return dayjs().format('YYYY-MM-DD')
}

function benefitsToRows(benefits: string[] | undefined): string[] {
  const rows = Array.isArray(benefits)
    ? benefits.map((benefit) => String(benefit || '').trim()).filter(Boolean)
    : []

  const rowCount = Math.min(5, Math.max(3, rows.length))

  return Array.from({ length: rowCount }, (_, index) => rows[index] ?? '')
}

function benefitRowsToItems(value: string[]): string[] {
  if (!Array.isArray(value)) {
    return []
  }

  return value.map((item) => item.replace(/^\s*[-*\u2022]\s*/, '').trim()).filter(Boolean)
}

export function getAppUpdateFormDefaults(appUpdate: AppUpdate | null = null): AppUpdateFormValues {
  return {
    title: appUpdate?.title ?? '',
    description: appUpdate?.description ?? '',
    description_html: appUpdate?.description_html ?? '',
    benefits: benefitsToRows(appUpdate?.benefits),
    audience: appUpdate?.audience ?? 'organizations',
    update_type: appUpdate?.update_type ?? 'new_feature',
    destination_url: appUpdate?.destination_url ?? '',
    scribe_url: appUpdate?.scribe_url ?? '',
    is_published: appUpdate?.is_published ?? true,
    update_date: appUpdate?.update_date ?? todayDateString(),
    thumbnail: appUpdate?.thumbnail ?? null,
    gallery: appUpdate?.gallery ?? [],
  }
}

/** One body for both create and update: it always sends `update_date` and both media fields. */
export function toAppUpdatePayload(values: AppUpdateFormValues): CreateAppUpdateInput {
  const thumbnailMediaId = values.thumbnail?.id ?? null

  return {
    title: String(values.title || '').trim(),
    description: cleanedValue(values.description),
    description_html: cleanedValue(values.description_html),
    benefits: benefitRowsToItems(values.benefits),
    audience: values.audience,
    update_type: values.update_type,
    destination_url: cleanedValue(values.destination_url),
    scribe_url: getScribeEmbedSrc(values.scribe_url),
    is_published: Boolean(values.is_published),
    update_date: cleanedValue(values.update_date) ?? todayDateString(),
    thumbnail_media_id: thumbnailMediaId,
    // The form schema requires every image to have an id before a save gets here.
    gallery_media_ids: (values.gallery ?? []).flatMap((media) =>
      media.id !== undefined && Number(media.id) !== Number(thumbnailMediaId) ? [media.id] : []
    ),
  }
}
